import { useState, useEffect, useRef } from "react";

// Existing values default to the 12-hour editor, while values entered with an
// hour above 12 stay in the 24-hour editor.
export function parseValue(value, use24Hour = false) {
  if (!value) return { hour: "", minute: "", ampm: "AM" };
  const [h, m] = value.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  if (use24Hour) {
    return { hour: String(h).padStart(2, "0"), minute: String(m).padStart(2, "0"), ampm };
  }
  let hour12 = h % 12;
  if (hour12 === 0) hour12 = 12;
  return { hour: String(hour12), minute: String(m).padStart(2, "0"), ampm };
}

// Accept either a 12-hour value with AM/PM or a 24-hour value from 13 through
// 24. The saved schedule value remains canonical "HH:MM"; hour 24 is stored as 00.
export function composeValue(hour, minute, ampm) {
  const enteredHour = parseInt(hour, 10);
  const m = parseInt(minute, 10);
  if (!Number.isInteger(enteredHour) || enteredHour < 1 || enteredHour > 24) return "";
  if (!Number.isInteger(m) || m < 0 || m > 59) return "";

  let h;
  if (enteredHour > 12) {
    h = enteredHour === 24 ? 0 : enteredHour;
  } else {
    h = enteredHour % 12;
    if (ampm === "PM") h += 12;
  }
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function getSuggestedAmPm(hour, currentAmPm) {
  const enteredHour = parseInt(hour, 10);
  if ((enteredHour >= 1 && enteredHour <= 5) || enteredHour === 12) return "PM";
  if (enteredHour >= 7 && enteredHour <= 11) return "AM";
  return currentAmPm;
}

// Hour and minute are typed inputs. Entering 13-24 switches this cell to a
// 24-hour display; entering 1-12 switches it back to the AM/PM display.
export default function TimeEntryCell({ value, onSave, label = "Time" }) {
  const initial = parseValue(value);
  const [draftHour, setDraftHour] = useState(initial.hour);
  const [draftMinute, setDraftMinute] = useState(initial.minute);
  const [localAmPm, setLocalAmPm] = useState(initial.ampm);
  const [use24Hour, setUse24Hour] = useState(false);
  const use24HourRef = useRef(use24Hour);
  const hourWasEditedRef = useRef(false);
  use24HourRef.current = use24Hour;

  useEffect(() => {
    const parsed = parseValue(value, use24HourRef.current);
    setDraftHour((current) => (
      use24HourRef.current && current === "24" && parsed.hour === "00" ? current : parsed.hour
    ));
    setDraftMinute(parsed.minute);
    setLocalAmPm(parsed.ampm);
  }, [value]);

  const displayed = parseValue(value, use24Hour);

  const commitHour = (h, ampm = localAmPm) => {
    const composed = composeValue(h, displayed.minute || "0", ampm);
    if (composed) onSave(composed);
  };
  const commitMinute = (m) => {
    const composed = composeValue(draftHour || "12", m, localAmPm);
    if (composed) onSave(composed);
  };
  const toggleAmPm = () => {
    const next = localAmPm === "AM" ? "PM" : "AM";
    setLocalAmPm(next);
    const composed = composeValue(displayed.hour || "12", displayed.minute || "0", next);
    if (composed) onSave(composed);
  };

  return (
    <div className="flex items-center gap-0.5">
      <input type="text" inputMode="numeric" value={draftHour} placeholder="--"
        aria-label={`${label} hour`}
        onFocus={(e) => e.target.select()}
        onChange={(e) => {
          const next = e.target.value.replace(/\D/g, "").slice(0, 2);
          const numericHour = Number(next);
          hourWasEditedRef.current = true;
          setDraftHour(next);
          if (next && numericHour >= 13 && numericHour <= 24) setUse24Hour(true);
          else if (next && numericHour >= 1 && numericHour <= 12) setUse24Hour(false);
        }}
        onBlur={() => {
          if (!hourWasEditedRef.current) return;
          hourWasEditedRef.current = false;
          const suggestedAmPm = getSuggestedAmPm(draftHour, localAmPm);
          setLocalAmPm(suggestedAmPm);
          commitHour(draftHour, suggestedAmPm);
        }}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        style={{ width: "2ch" }}
        className="bg-transparent border-none outline-none text-gray-300 text-sm p-0 text-center" />
      <span className="text-gray-600">:</span>
      <input type="text" inputMode="numeric" value={draftMinute} placeholder="--"
        aria-label={`${label} minute`}
        onFocus={(e) => e.target.select()}
        onChange={(e) => setDraftMinute(e.target.value.replace(/\D/g, "").slice(0, 2))}
        onBlur={() => { if (draftMinute !== displayed.minute) commitMinute(draftMinute); }}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        style={{ width: "2ch" }}
        className="bg-transparent border-none outline-none text-gray-300 text-sm p-0 text-center" />
      {!use24Hour && (
        <button type="button" onClick={toggleAmPm} aria-label={`${label} ${localAmPm}; switch to ${localAmPm === "AM" ? "PM" : "AM"}`}
          className="ml-1 text-[10px] font-bold text-gray-500 hover:text-gray-300 bg-transparent border-none cursor-pointer p-0">
          {localAmPm}
        </button>
      )}
    </div>
  );
}
